import {
  type JobDetailsView,
  useJobDetailsProps,
} from '@seek/job-details-view';
import { useMemo } from 'react';

import { useJDVSkillsMatchProps } from 'src/components/JobDetailsPage/useJDVSkillsMatchProps';
import { useNumberOfApplicants } from 'src/components/JobDetailsPage/useNumberOfApplicants';
import { useSkillsMatchTracking } from 'src/components/JobDetailsPage/useSkillsMatchTracking';
import type { ApplicantCount } from 'src/graphql/graphql';
import { useSplitViewContext } from 'src/handlers/SplitViewContext';
import { useIsPDPAConsentRequired } from 'src/hooks/useIsPDPAConsentRequired';
import { useReturnUrl } from 'src/hooks/useReturnUrl';
import { getUseSaveJobCompetitiveJob } from 'src/hooks/useSaveJob/useSaveJobCompetitiveJob';
import {
  useSaveJobJDV,
  type UseSaveJobJDVProps,
} from 'src/hooks/useSaveJob/useSaveJobJDV';
import { useSolHash } from 'src/hooks/useSolHash';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useCandidateDetails } from 'src/modules/hooks/useCandidateDetailsQuery';
import { useEnableJdvJobMode } from 'src/store/featureFlags/hooks';
import type { JobDetails } from 'src/store/jobdetails/types';
import { useSelector } from 'src/store/react';
import {
  selectAuthenticated,
  selectIsJDP,
  selectJobDetailsPersonalised,
  selectResults,
} from 'src/store/selectors';

import buildJobDetailsSEOLinks from './buildJobDetailsSEOLinks';
import { useApplyTracking } from './useApplyTracking';
import { useCompetitivePlacementJobs } from './useCompetitivePlacementJobs';
import useJDVTracking from './useJDVTracking';
import { useJobDetailsSolMetadataReference } from './useJobDetailsSolMetadataReference';
import useReportJobAd from './useReportJobAd';

interface UsePropsForJobDetailsViewProps {
  jobDetails: JobDetails;
  options?: {
    origin?: string;
    position?: number;
    sellingPoints?: number;
    solHash?: string;
    competitivePlacement?: {
      openInNewTab?: boolean;
      enabled?: boolean;
    };
  };
  savedJobMutation?: UseSaveJobJDVProps['savedJobMutation'];
}

const useMapJobIdToJobDisplayType = (jobId: string) => {
  const jobs = useSelector(selectResults)?.jobs;
  return jobs?.find((job) => job.id === jobId)?.displayType;
};

export const usePropsForJobDetailsView = ({
  jobDetails,
  options: {
    origin: jobOrigin,
    position: jobPosition,
    sellingPoints: jobSellingPoints,
    solHash,
    competitivePlacement: { openInNewTab, enabled } = {},
  } = {},
  savedJobMutation,
}: UsePropsForJobDetailsViewProps): React.ComponentProps<
  typeof JobDetailsView
> => {
  const jobId = jobDetails.job.id;
  const { email } = useCandidateDetails();
  const isAuthenticated = useSelector(selectAuthenticated);
  const enableJdvJobMode = useEnableJdvJobMode();

  const jobDetailsPersonalisedFromRedux = useSelector(
    selectJobDetailsPersonalised,
  );
  const jobDetailsPersonalised =
    jobDetails.personalised || jobDetailsPersonalisedFromRedux;

  const isPDPAConsentRequired =
    useIsPDPAConsentRequired().isPDPAConsentRequired;
  const solMetadataReference =
    useJobDetailsSolMetadataReference(jobDetails) || solHash;

  const [, solMetadata] = useSolHash({
    id: jobId,
    solMetadataReference,
  });

  const displayType = useMapJobIdToJobDisplayType(jobId);

  const onAnalyticsEvent = useJDVTracking();
  const onReportJobAd = useReportJobAd(jobDetails?.job?.id);

  const registerUrl = useReturnUrl({ mode: 'register' });
  const signInUrl = useReturnUrl({ mode: 'signin' });

  const isHirerVerified = jobDetails?.job?.advertiser?.isVerified ?? false;

  const qualities = useJDVSkillsMatchProps(jobId);

  useSkillsMatchTracking({ qualities, jobId, solMetadata });

  const { applicationVolumeLabel, enableNumberOfApplicants } =
    useNumberOfApplicants(
      (jobDetails.insights?.[0] as ApplicantCount) ?? {
        countLabel: '',
        volumeLabel: '',
      },
    );

  const [applyUrl, onApplyClick] = useApplyTracking({
    jobDetails,
    jobDetailsPersonalised,
    solMetadataReference,
    isHirerVerified,
    isMatchedSkillsWidgetShown: qualities.shouldShowSkillsMatch,
    isMatchedSkillsNudgeShown: !isAuthenticated,
    jobOrigin,
    jobSellingPoints,
  });

  const analyticsFacade = useAnalyticsFacade();
  const { selectedJobPosition } = useSplitViewContext();

  const trackVerifiedHirerBadgeClick = (linkSection: string) => {
    analyticsFacade.verifiedHirerBadgeClicked({
      jobId,
      jobListingPosition: jobPosition || selectedJobPosition || -1, // -1 is the default value when job is in standalone view
      linkSection,
    });
  };

  const trackMoreAboutThisCompanyClick = (linkSection: string) => {
    analyticsFacade.companyProfileWidgetLinkClicked({
      linkSection,
    });
  };

  const { toggleSavedJob, isSaved } = useSaveJobJDV({
    jobId: jobDetails?.job?.id,
    solMetadataReference,
    savedJobMutation,
  });

  const result = useJobDetailsProps({
    flags: {
      isSaved:
        jobDetailsPersonalised?.isSaved === isSaved
          ? jobDetailsPersonalised.isSaved
          : isSaved,
      appliedDateFormatted:
        jobDetailsPersonalised?.appliedDateTime?.shortAbsoluteLabel,
      isAuthenticated,
    },
    topApplicant: jobDetailsPersonalised?.topApplicantBadge2,
    jobApplyUrl: applyUrl,
    jobDetails,
    salaryMatch: jobDetailsPersonalised?.salaryMatch,
  });

  // temp fix for https://myseek.atlassian.net/browse/CTCT-756
  // should remove after JdvJobModeFeatureFlag is fully released
  if (!enableJdvJobMode) {
    result.job.workArrangementLabel = undefined;
  }

  const isJobDetailsStandalone = useSelector(selectIsJDP);

  const {
    classification,
    normalisedRoleTitle,
    subClassification,
    where,
    workType,
  } = jobDetails.seoInfo || {};

  const seoInfo = useMemo(
    () =>
      buildJobDetailsSEOLinks({
        normalisedRoleTitle,
        where,
        workType,
        classification,
        subClassification,
      }),
    [classification, subClassification, where, workType, normalisedRoleTitle],
  );

  const competitivePlacement = useCompetitivePlacementJobs({
    jobDetailsId: jobId,
    openInNewTab,
    shouldSkip: !enabled,
  });
  const useCompetitivePlacementToggleSaveJob = Boolean(
    competitivePlacement.jobs,
  )
    ? getUseSaveJobCompetitiveJob(competitivePlacement.jobs || [])
    : undefined;

  return {
    isPDPAConsentRequired,
    job: result.job,
    ctas: {
      onApplyClick,
      onToggleSaveJob: toggleSavedJob,
      onReportJobAd,
      trackVerifiedHirerBadgeClick,
      trackMoreAboutThisCompanyClick,
    },
    reportJobAdInitialValues: {
      email,
      reason: '',
      comment: '',
    },
    onAnalyticsEvent,
    uniqueContainerValue: 'job-details-page',
    shouldOpenApplyInNewTab: !isJobDetailsStandalone,
    // This should be removed in metropolis after
    // https://myseek.atlassian.net/browse/DCS-9549
    hideOpenInNewTab: true,
    displayCompanyProfile: true,
    renderRelativePaths: true,
    jobUrlOpts: {
      hide: isJobDetailsStandalone,
      params: {
        ref: 'search-standalone',
        type: displayType,
        origin: true,
      },
      meta: {
        solMetadataReference,
      },
    },
    registerUrl,
    signInUrl,
    isLoggedIn: isAuthenticated,
    qualities,
    competitivePlacement,
    useCompetitivePlacementToggleSaveJob,
    applicationVolumeLabel,
    featureFlags: {
      enableVerifiedHirerBadge: true,
      enableReducedDuplicatedApply: true,
      stickApplyButtonOnDesktop: true,
      enableMatchedQualities: true,
      hideApplyButtonOnPrivateAdvertiser: true,
      enableNumberOfApplicants,
    },
    seoInfo,
  };
};
